/**
 * A tiny keyed query cache (no external dependency).
 *
 * - Keys are arrays (`['site', siteId, 'pages']`); invalidation works by key prefix.
 * - Concurrent fetches of the same key are deduplicated.
 * - Invalidating a key marks it stale and immediately refetches it when something is subscribed;
 *   unsubscribed entries are refetched the next time a component asks for them.
 * - `patch()` applies an optimistic update and returns a rollback function.
 *
 * The class is framework-agnostic (pure TS) so it can be unit-tested without React.
 */

export type QueryKey = readonly unknown[];
export type QueryStatus = 'idle' | 'loading' | 'success' | 'error';
export type Fetcher<T> = (signal: AbortSignal) => Promise<T>;

export interface QueryState<T> {
  data: T | undefined;
  error: unknown;
  status: QueryStatus;
  /** A request is in flight (initial load or background refetch). */
  isFetching: boolean;
  /** Data was invalidated and should be refetched. */
  stale: boolean;
  updatedAt: number;
}

interface Entry {
  key: QueryKey;
  state: QueryState<unknown>;
  listeners: Set<() => void>;
  fetcher: Fetcher<unknown> | null;
  inflight: Promise<unknown> | null;
  controller: AbortController | null;
  /** Bumped on every write so late responses of superseded requests are ignored. */
  version: number;
}

export interface QueryCacheOptions {
  /** Data younger than this is served without refetching on mount (ms). */
  staleTime?: number;
  now?: () => number;
}

const IDLE: QueryState<never> = Object.freeze({ data: undefined, error: null, status: 'idle', isFetching: false, stale: false, updatedAt: 0 });

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      const v = (value as Record<string, unknown>)[k];
      if (v !== undefined) out[k] = stableValue(v);
    }
    return out;
  }
  return value;
}

/** Deterministic hash of a key: object keys are sorted and `undefined` members are dropped. */
export function hashKey(key: QueryKey): string {
  return JSON.stringify(stableValue(key));
}

/** True when `key` starts with every element of `prefix` (deep equality per element). */
export function keyMatches(key: QueryKey, prefix: QueryKey): boolean {
  if (prefix.length > key.length) return false;
  return prefix.every((part, i) => hashKey([part]) === hashKey([key[i]]));
}

export class QueryCache {
  private entries = new Map<string, Entry>();
  private readonly staleTime: number;
  private readonly now: () => number;

  constructor(opts: QueryCacheOptions = {}) {
    this.staleTime = opts.staleTime ?? 30_000;
    this.now = opts.now ?? (() => Date.now());
  }

  private entry(key: QueryKey): Entry {
    const hash = hashKey(key);
    let entry = this.entries.get(hash);
    if (!entry) {
      entry = { key, state: IDLE, listeners: new Set(), fetcher: null, inflight: null, controller: null, version: 0 };
      this.entries.set(hash, entry);
    }
    return entry;
  }

  private write(entry: Entry, patch: Partial<QueryState<unknown>>): void {
    entry.state = { ...entry.state, ...patch };
    entry.listeners.forEach((l) => l());
  }

  getState<T>(key: QueryKey): QueryState<T> {
    return (this.entries.get(hashKey(key))?.state ?? IDLE) as QueryState<T>;
  }

  getData<T>(key: QueryKey): T | undefined {
    return this.getState<T>(key).data;
  }

  subscribe(key: QueryKey, listener: () => void): () => void {
    const entry = this.entry(key);
    entry.listeners.add(listener);
    return () => {
      entry.listeners.delete(listener);
    };
  }

  /** Remember the latest fetcher for a key (used by invalidation refetches). */
  register<T>(key: QueryKey, fetcher: Fetcher<T>): void {
    this.entry(key).fetcher = fetcher as Fetcher<unknown>;
  }

  /** Whether a mounted consumer should (re)fetch: no data yet, invalidated, or older than staleTime. */
  shouldFetch(key: QueryKey): boolean {
    const entry = this.entries.get(hashKey(key));
    if (!entry) return true;
    if (entry.inflight) return false;
    const { status, stale, updatedAt } = entry.state;
    if (status === 'idle' || stale) return true;
    if (status === 'error') return false;
    return this.now() - updatedAt > this.staleTime;
  }

  fetch<T>(key: QueryKey, fetcher?: Fetcher<T>, opts: { force?: boolean } = {}): Promise<T> {
    const entry = this.entry(key);
    if (fetcher) entry.fetcher = fetcher as Fetcher<unknown>;
    const run = entry.fetcher;
    if (!run) return Promise.reject(new Error(`No fetcher registered for ${hashKey(key)}`));
    if (entry.inflight && !opts.force) return entry.inflight as Promise<T>;
    entry.controller?.abort();
    const controller = new AbortController();
    entry.controller = controller;
    const version = ++entry.version;
    this.write(entry, { isFetching: true, status: entry.state.data === undefined ? 'loading' : 'success' });
    const promise = run(controller.signal).then(
      (data) => {
        if (entry.version === version) {
          entry.inflight = null;
          entry.controller = null;
          this.write(entry, { data, error: null, status: 'success', isFetching: false, stale: false, updatedAt: this.now() });
        }
        return data as T;
      },
      (error: unknown) => {
        if (entry.version === version) {
          entry.inflight = null;
          entry.controller = null;
          this.write(entry, { error, status: entry.state.data === undefined ? 'error' : 'success', isFetching: false });
        }
        throw error;
      },
    );
    entry.inflight = promise;
    // Consumers read errors from state; avoid unhandled rejections for fire-and-forget fetches.
    promise.catch(() => undefined);
    return promise;
  }

  /** Replace data (server reconciliation or optimistic update). Cancels nothing; bumps the version. */
  setData<T>(key: QueryKey, updater: T | undefined | ((prev: T | undefined) => T | undefined)): void {
    const entry = this.entry(key);
    const prev = entry.state.data as T | undefined;
    const next = typeof updater === 'function' ? (updater as (p: T | undefined) => T | undefined)(prev) : updater;
    entry.version++;
    entry.inflight = null;
    entry.controller?.abort();
    entry.controller = null;
    this.write(entry, {
      data: next,
      error: null,
      status: next === undefined ? 'idle' : 'success',
      isFetching: false,
      updatedAt: this.now(),
    });
  }

  /** Optimistically update every cached entry matching `prefix`; returns a rollback restoring previous data. */
  patch<T>(prefix: QueryKey, updater: (prev: T) => T): () => void {
    const snapshots: Array<{ key: QueryKey; data: unknown }> = [];
    for (const entry of this.entries.values()) {
      if (!keyMatches(entry.key, prefix) || entry.state.data === undefined) continue;
      snapshots.push({ key: entry.key, data: entry.state.data });
      this.setData<T>(entry.key, (prev) => (prev === undefined ? prev : updater(prev)));
    }
    return () => snapshots.forEach(({ key, data }) => this.setData(key, data));
  }

  /** Mark matching entries stale; refetch the ones currently in use. Returns the number of matched entries. */
  invalidate(prefix: QueryKey): number {
    let count = 0;
    for (const entry of this.entries.values()) {
      if (!keyMatches(entry.key, prefix)) continue;
      count++;
      this.write(entry, { stale: true });
      if (entry.listeners.size > 0 && entry.fetcher) void this.fetch(entry.key, undefined, { force: true }).catch(() => undefined);
    }
    return count;
  }

  /** Drop matching entries entirely (e.g. after deleting a resource). */
  remove(prefix: QueryKey): void {
    for (const [hash, entry] of this.entries) {
      if (!keyMatches(entry.key, prefix)) continue;
      entry.controller?.abort();
      if (entry.listeners.size > 0) {
        entry.version++;
        entry.inflight = null;
        this.write(entry, { ...IDLE });
      } else this.entries.delete(hash);
    }
  }

  /** Forget everything (sign-out, workspace switch). */
  clear(): void {
    this.remove([]);
  }

  keys(): QueryKey[] {
    return [...this.entries.values()].map((e) => e.key);
  }
}
